import { logger } from "@/lib/logger";
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { updateDynamicPwaBranding } from '@/utils/dynamicManifest';

interface SiteContextData {
  id: string;
  slug: string;
  display_name: string;
  logo_url: string | null;
  primary_color: string;
  favicon_url: string | null;
  type: 'trial_site' | 'cro' | 'sponsor' | 'phc' | 'government' | 'training_cohort';
  linked_trial_site_id?: string;
  contact_email: string | null;
  contact_phone: string | null;
  contact_name: string | null;
  meta_description: string | null;
  nra_role?: string | null;
  nra_slug?: string | null;
}

// Default trial site branding (teal/clinical theme)
const DEFAULT_TRIAL_SITE_LOGO = '/trial-site-default-logo.png';
const DEFAULT_TRIAL_SITE_COLOR = '#0891b2'; // Teal for clinical/medical feel

// Default CRO branding (professional blue)
const DEFAULT_CRO_LOGO = '/cro-default-logo.png';
const DEFAULT_CRO_COLOR = '#2563eb'; // Blue for CRO professional feel

// Default Sponsor branding (deep indigo)
const DEFAULT_SPONSOR_LOGO = '/sponsor-default-logo.png';
const DEFAULT_SPONSOR_COLOR = '#4f46e5'; // Indigo for sponsor executive feel

// Default PHC branding (green for community health)
const DEFAULT_PHC_LOGO = '/phc-default-logo.png';
const DEFAULT_PHC_COLOR = '#16a34a'; // Green for PHC/community health

// Supported domains for white-label subdomains
const SUPPORTED_DOMAINS = [
  'pharmatrialconnect.com', 
  'pharmatrialconnect.africa',
  'pharmatrialconnect.health',
  'pharmatrialconnect.org',
  'pharmatrialconnect.id',
  'pharmatrialconnect.in',
  // Training / white-label cohort classrooms (auto-registered per cohort)
  'pharmatrialconnect.academy',
];

// Default training classroom branding (academy indigo)
const DEFAULT_TRAINING_LOGO = '/trial-site-default-logo.png';
const DEFAULT_TRAINING_COLOR = '#7c3aed';

interface SiteContextValue {
  site: SiteContextData | null;
  isLoading: boolean;
  subdomainNotFound: boolean;
  detectedSubdomain: string | null;
}

const SiteContext = createContext<SiteContextValue>({ 
  site: null, 
  isLoading: true,
  subdomainNotFound: false,
  detectedSubdomain: null,
});

export function useSiteContext() {
  const context = useContext(SiteContext);
  return context;
}

/**
 * Extract subdomain AND the matched TLD from hostname
 * e.g. "et.pharmatrialconnect.africa" → { subdomain: "et", tld: "africa" }
 */
function extractSubdomainInfo(hostname: string): { subdomain: string; tld: string } | null {
  const lowerHostname = hostname.toLowerCase();
  
  for (const domain of SUPPORTED_DOMAINS) {
    if (
      lowerHostname.includes(domain) &&
      lowerHostname !== domain &&
      lowerHostname !== `www.${domain}`
    ) {
      const subdomain = lowerHostname.split('.')[0];
      if (subdomain && subdomain !== 'www') {
        // Extract TLD from the domain (e.g. "pharmatrialconnect.africa" → "africa")
        const tld = domain.replace('pharmatrialconnect.', '');
        return { subdomain, tld };
      }
    }
  }
  
  return null;
}


export function SiteProvider({ children }: { children: React.ReactNode }) {
  const [site, setSite] = useState<SiteContextData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subdomainNotFound, setSubdomainNotFound] = useState(false);
  const [detectedSubdomain, setDetectedSubdomain] = useState<string | null>(null);

  useEffect(() => {
    const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

    const getCachedSite = (subdomain: string): SiteContextData | null => {
      try {
        const raw = sessionStorage.getItem(`site_config_${subdomain}`);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (Date.now() - cached._cachedAt > CACHE_TTL_MS) {
          sessionStorage.removeItem(`site_config_${subdomain}`);
          return null;
        }
        return cached.data as SiteContextData;
      } catch {
        return null;
      }
    };

    const setCachedSite = (subdomain: string, data: SiteContextData) => {
      try {
        sessionStorage.setItem(`site_config_${subdomain}`, JSON.stringify({ data, _cachedAt: Date.now() }));
      } catch {
        // sessionStorage full or unavailable — silently ignore
      }
    };

    const detectAndLoadSite = async () => {
      const hostname = window.location.hostname;
      const subdomainInfo = extractSubdomainInfo(hostname);
      const subdomain = subdomainInfo?.subdomain || null;
      const detectedTld = subdomainInfo?.tld || null;
      
      // Store the detected subdomain for error display
      setDetectedSubdomain(subdomain);
      
      logger.debug('[SiteContext] Starting detection:', { hostname, subdomain, detectedTld });
      
      // No subdomain = not a white-label site
      if (!subdomain) {
        logger.debug('[SiteContext] No subdomain detected - not a white-label site');
        setSite(null);
        setSubdomainNotFound(false);
        setIsLoading(false);
        return;
      }

      // Check sessionStorage cache first (include TLD in cache key to avoid cross-TLD collisions)
      const cacheKey = detectedTld ? `${subdomain}_${detectedTld}` : subdomain;
      const cached = getCachedSite(cacheKey);
      if (cached) {
        logger.debug(`[SiteContext] Cache hit for subdomain: ${subdomain} (tld: ${detectedTld})`);
        setSite(cached);
        setSubdomainNotFound(false);
        applyBranding(cached);
        setIsLoading(false);
        return;
      }
      
      try {
        logger.debug(`[SiteContext] Detected subdomain: ${subdomain} (tld: ${detectedTld}) from ${hostname}`);

        // Tier 0: .academy — training cohort classrooms. Every cohort row carries an
        // auto-generated academy_slug, so new classroom subdomains resolve with no manual step.
        if (detectedTld === 'academy') {
          const { data: cohortRows, error: cohortError } = await supabase
            .rpc('get_public_training_cohort_by_slug' as any, { p_slug: subdomain });
          const cohortData = (cohortRows as any[])?.[0] || null;

          if (!cohortError && cohortData) {
            const cohortSite: SiteContextData = {
              id: cohortData.id,
              slug: cohortData.academy_slug || subdomain,
              display_name: cohortData.display_name || subdomain,
              logo_url: cohortData.brand_logo_url || DEFAULT_TRAINING_LOGO,
              primary_color: DEFAULT_TRAINING_COLOR,
              favicon_url: null,
              type: 'training_cohort',
              contact_email: null,
              contact_phone: null,
              contact_name: null,
              meta_description: null,
            };
            setSite(cohortSite);
            setSubdomainNotFound(false);
            applyBranding(cohortSite);
            setCachedSite(cacheKey, cohortSite);
            setIsLoading(false);
            return;
          }

          logger.debug(`[SiteContext] No training cohort classroom for slug: ${subdomain}`);
          setSite(null);
          setSubdomainNotFound(true);
          setIsLoading(false);
          return;
        }


        // Use SECURITY DEFINER RPC — TLD-aware resolution
        const { data: siteRows, error: siteError } = await supabase
          .rpc('get_public_trial_site_info', { site_slug: subdomain, p_tld: detectedTld || undefined });

        const siteData = siteRows && siteRows.length > 0 ? siteRows[0] : null;

        if (!siteError && siteData) {
          logger.debug(`[SiteContext] Found trial site: ${siteData.display_name || siteData.name} (status: ${siteData.status})`);
          const siteWithDefaults: SiteContextData = {
            id: siteData.id,
            slug: siteData.slug || subdomain,
            display_name: siteData.display_name || siteData.name || subdomain,
            logo_url: siteData.logo_url || DEFAULT_TRIAL_SITE_LOGO,
            primary_color: siteData.primary_color || DEFAULT_TRIAL_SITE_COLOR,
            favicon_url: siteData.favicon_url || null,
            type: 'trial_site' as const,
            contact_email: siteData.primary_contact_email || null,
            contact_phone: siteData.primary_contact_phone || null,
            contact_name: siteData.primary_contact_name || null,
            meta_description: siteData.meta_description || null,
          };
          
          setSite(siteWithDefaults);
          setSubdomainNotFound(false);
          applyBranding(siteWithDefaults);
          setCachedSite(cacheKey, siteWithDefaults);
          setIsLoading(false);
          return;
        }

        // Check if site exists but is deactivated (also TLD-aware)
        const { data: inactiveRows } = await supabase
          .rpc('check_inactive_trial_site', { site_slug: subdomain, p_tld: detectedTld || undefined });

        const inactiveSite = inactiveRows && inactiveRows.length > 0 ? inactiveRows[0] : null;

        if (inactiveSite) {
          logger.debug(`[SiteContext] Site "${subdomain}" exists but is deactivated (status: ${inactiveSite.status})`);
          setSite(null);
          setSubdomainNotFound(true);
          setIsLoading(false);
          return;
        }

        // If no trial site found, check for CRO organization with this slug (TLD-aware)
        const { data: croRows, error: croError } = await supabase
          .rpc('get_public_org_by_slug', { org_slug: subdomain, org_type: 'cro', p_tld: detectedTld || undefined });
        const croData = croRows && croRows.length > 0 ? croRows[0] : null;

        if (!croError && croData) {
          logger.debug(`[SiteContext] Found CRO organization: ${croData.name}`);
          const croSite: SiteContextData = {
            id: croData.id,
            slug: croData.slug || subdomain,
            display_name: croData.name,
            logo_url: croData.logo_url || DEFAULT_CRO_LOGO,
            primary_color: croData.primary_color || DEFAULT_CRO_COLOR,
            favicon_url: croData.favicon_url || null,
            type: 'cro',
            contact_email: croData.email || null,
            contact_phone: croData.phone || null,
            contact_name: null,
            meta_description: null,
          };
          
          setSite(croSite);
          setSubdomainNotFound(false);
          applyBranding(croSite);
          setCachedSite(cacheKey, croSite);
          setIsLoading(false);
          return;
        }

        // If no CRO found, check for Sponsor organization with this slug (TLD-aware)
        const { data: sponsorRows, error: sponsorError } = await supabase
          .rpc('get_public_org_by_slug', { org_slug: subdomain, org_type: 'sponsor', p_tld: detectedTld || undefined });
        const sponsorData = sponsorRows && sponsorRows.length > 0 ? sponsorRows[0] : null;

        if (!sponsorError && sponsorData) {
          logger.debug(`[SiteContext] Found Sponsor organization: ${sponsorData.name}`);
          const sponsorSite: SiteContextData = {
            id: sponsorData.id,
            slug: sponsorData.slug || subdomain,
            display_name: sponsorData.name,
            logo_url: sponsorData.logo_url || DEFAULT_SPONSOR_LOGO,
            primary_color: sponsorData.primary_color || DEFAULT_SPONSOR_COLOR,
            favicon_url: sponsorData.favicon_url || null,
            type: 'sponsor',
            contact_email: sponsorData.email || null,
            contact_phone: sponsorData.phone || null,
            contact_name: null,
            meta_description: null,
          };
          
          setSite(sponsorSite);
          setSubdomainNotFound(false);
          applyBranding(sponsorSite);
          setCachedSite(cacheKey, sponsorSite);
          setIsLoading(false);
          return;
        }

        // Tier 4: PHC site link — TLD-aware resolution
        const { data: phcRows, error: phcError } = await supabase
          .rpc('get_public_phc_site_link', { p_slug: subdomain, p_tld: detectedTld || undefined });
        const phcData = phcRows && phcRows.length > 0 ? phcRows[0] : null;

        if (!phcError && phcData) {
          logger.debug(`[SiteContext] Found PHC site link: ${phcData.phc_facility_name}`);
          const phcSite: SiteContextData = {
            id: phcData.id,
            slug: phcData.phc_slug,
            display_name: phcData.phc_facility_name,
            logo_url: phcData.logo_url || DEFAULT_PHC_LOGO,
            primary_color: phcData.primary_color || DEFAULT_PHC_COLOR,
            favicon_url: phcData.favicon_url || null,
            type: 'phc',
            linked_trial_site_id: phcData.trial_site_id,
            contact_email: null,
            contact_phone: null,
            contact_name: null,
            meta_description: null,
          };

          setSite(phcSite);
          setSubdomainNotFound(false);
          applyBranding(phcSite);
          setCachedSite(cacheKey, phcSite);
          setIsLoading(false);
          return;
        }


        // Tier 5: Government organization — TLD-aware
        const { data: govRows, error: govError } = await supabase
          .rpc('get_public_org_by_slug', { org_slug: subdomain, org_type: 'government', p_tld: detectedTld || undefined });
        const govData = govRows && govRows.length > 0 ? govRows[0] : null;

        if (!govError && govData) {
          logger.debug(`[SiteContext] Found Government organization: ${govData.name}`);
          const govSite: SiteContextData = {
            id: govData.id,
            slug: govData.slug || subdomain,
            display_name: govData.name,
            logo_url: govData.logo_url || DEFAULT_PHC_LOGO,
            primary_color: govData.primary_color || '#eab308',
            favicon_url: govData.favicon_url || null,
            type: 'government',
            contact_email: govData.email || null,
            contact_phone: govData.phone || null,
            contact_name: null,
            meta_description: null,
            nra_role: (govData as any).nra_role || null,
            nra_slug: (govData as any).nra_slug || null,
          };

          setSite(govSite);
          setSubdomainNotFound(false);
          applyBranding(govSite);
          setCachedSite(cacheKey, govSite);
          setIsLoading(false);
          return;
        }

        // No match across all tiers
        logger.debug(`[SiteContext] No site found for subdomain: ${subdomain}`);
        setSite(null);
        setSubdomainNotFound(true);
        setIsLoading(false);
      } catch (error) {
        logger.error('[SiteContext] Error detecting site context:', error);
        setSite(null);
        setSubdomainNotFound(true);
        setIsLoading(false);
      }
    };

    const applyBranding = (siteData: SiteContextData) => {
      // Apply branding immediately
      const portalLabel = siteData.type === 'cro' ? 'CRO Portal' : siteData.type === 'sponsor' ? 'Sponsor Portal' : siteData.type === 'phc' ? 'Community Health' : siteData.type === 'government' ? 'Government Surveillance' : siteData.type === 'training_cohort' ? 'Training Classroom' : 'Clinical Trials';
      document.title = `${siteData.display_name} | ${portalLabel}`;
      
      // Apply primary color as CSS variable
      document.documentElement.style.setProperty('--trial-site-primary', siteData.primary_color);
      document.documentElement.style.setProperty('--site-primary', siteData.primary_color);
      
      // Update favicon
      if (siteData.favicon_url) {
        const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (link) {
          link.href = siteData.favicon_url;
        } else {
          const newLink = document.createElement('link');
          newLink.rel = 'icon';
          newLink.href = siteData.favicon_url;
          document.head.appendChild(newLink);
        }
      }

      // Per-site home-screen icon (site type + monogram + PTC)
      updateDynamicPwaBranding({
        slug: siteData.slug,
        type: siteData.type,
        primary_color: siteData.primary_color,
      });
    };

    detectAndLoadSite();
  }, []);

  return (
    <SiteContext.Provider value={{ site, isLoading, subdomainNotFound, detectedSubdomain }}>
      {children}
    </SiteContext.Provider>
  );
}
